'use client';

import { useState, useEffect, useCallback } from 'react';

const SLIDES = [
  {
    image: 'https://mw8xfjtwnctw6euk.public.blob.vercel-storage.com/demo-eye-anatomy.png',
    answer: 'Ciliary body',
    hint: 'Controls lens shape for accommodation — produces aqueous humor',
    source: 'Ocular Anatomy',
  },
  {
    image: 'https://mw8xfjtwnctw6euk.public.blob.vercel-storage.com/demo-hyperopia.png',
    answer: 'Hyperopia',
    hint: 'Light focuses behind the retina — also called farsightedness',
    source: 'Disorders of the Eye',
  },
  {
    image: 'https://mw8xfjtwnctw6euk.public.blob.vercel-storage.com/demo-focal-plane.png',
    answer: 'Focal plane',
    hint: 'The plane where light converges after refraction',
    source: 'Disorders of the Eye',
  },
  {
    image: 'https://mw8xfjtwnctw6euk.public.blob.vercel-storage.com/demo-astigmatism.png',
    answer: 'Astigmatism',
    hint: 'Caused by irregular curvature of the cornea',
    source: 'Disorders of the Eye',
  },
];

export const OcclusionShowcase = () => {
  const [active, setActive] = useState(0);
  const [prev, setPrev] = useState<number | null>(null);
  const [transitioning, setTransitioning] = useState(false);

  const goTo = useCallback((next: number) => {
    if (next === active || transitioning) return;
    setPrev(active);
    setActive(next);
    setTransitioning(true);
    setTimeout(() => {
      setPrev(null);
      setTransitioning(false);
    }, 500);
  }, [active, transitioning]);

  useEffect(() => {
    const interval = setInterval(() => {
      goTo((active + 1) % SLIDES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [active, goTo]);

  const slide = SLIDES[active];
  const prevSlide = prev !== null ? SLIDES[prev] : null;

  return (
    <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
      {/* Left — text */}
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-400/20 text-xs font-semibold mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
          <span className="text-blue-600">New feature</span>
        </div>

        <h2 className="text-3xl sm:text-4xl font-bold font-serif mb-5 text-foreground leading-tight">
          Your diagrams become flashcards.{' '}
          <span className="text-blue-600">Automatically.</span>
        </h2>

        <p className="text-muted leading-relaxed mb-8">
          Upload slides with labeled diagrams. Labels are detected automatically
          and turned into image occlusion cards — hiding one label at a time.
          Trivial labels are skipped. No manual work.
        </p>

        <div className="space-y-3 mb-8">
          {[
            'Every label detected with pixel-perfect accuracy',
            'Difficulty classified automatically — skips what you already know',
            'One diagram generates multiple testable cards',
          ].map((text) => (
            <div key={text} className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="text-sm text-foreground/70">{text}</span>
            </div>
          ))}
        </div>

        <p className="text-xs text-muted">
          Works with any labeled diagram
        </p>
      </div>

      {/* Right — slideshow */}
      <div>
        <div className="rounded-2xl overflow-hidden border border-border shadow-lg bg-white">
          {/* Fixed aspect ratio container prevents layout shift on mobile */}
          <div className="relative aspect-[3/2] bg-gray-50 overflow-hidden">
            {/* Previous slide (fading out) */}
            {prevSlide && (
              <img
                key={`prev-${prev}`}
                src={prevSlide.image}
                alt={`Occluded: ${prevSlide.answer}`}
                className="absolute inset-0 w-full h-full object-contain transition-opacity duration-500 ease-in-out opacity-0"
              />
            )}
            {/* Active slide (fading in) */}
            <img
              key={`active-${active}`}
              src={slide.image}
              alt={`Occluded: ${slide.answer}`}
              className="absolute inset-0 w-full h-full object-contain animate-fade-in"
            />
          </div>

          {/* Card footer — fixed height to prevent layout shift from different hint lengths */}
          <div className="px-5 py-4 border-t border-border bg-white h-24 overflow-hidden">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 rounded bg-blue-500 flex-shrink-0" />
                <span className="text-xs text-muted">What label is hidden?</span>
              </div>
              <span
                key={`answer-${active}`}
                className="text-sm font-bold text-blue-600 animate-fade-in"
              >
                {slide.answer}
              </span>
            </div>
            <div
              key={`hint-${active}`}
              className="text-xs text-muted italic animate-fade-in"
            >
              💡 {slide.hint}
            </div>
          </div>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-4">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className="group flex h-11 w-11 items-center justify-center rounded-full"
              aria-label={`Show image occlusion example ${i + 1}`}
              aria-pressed={i === active}
            >
              <span className={`h-2 rounded-full transition-all duration-300 ${
                i === active ? 'w-5 bg-blue-500' : 'w-2 bg-border group-hover:bg-muted/40'
              }`} />
            </button>
          ))}
        </div>

        <p
          key={`source-${active}`}
          className="text-center text-[10px] text-muted mt-2 animate-fade-in"
        >
          From: {slide.source}
        </p>
      </div>
    </div>
  );
};
